import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPets } from '../services/petService';
import { getVaccinations, addVaccination } from '../services/vaccinationService';
import { getClinic, Clinic } from '../services/clinicService';
import { Pet, Vaccination, SessionUser } from '../types';
import {
  Search, Syringe, Download, Printer, CheckCircle, Clock,
  Calendar, FileText, ShieldCheck, Stamp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AddVaccineModal from '../components/Modals/AddVaccineModal';
import PetAvatar from '../components/Avatar/PetAvatar';
import EmptyState from '../components/EmptyState/EmptyState';
import { getVaccinationStatus, getDueDate, isDone } from '../utils/vaccinationStatus';
import { normalizeTr } from '../utils/normalizeTr';

interface VaccineCardProps {
  user?: SessionUser;
}

const VaccineCard: React.FC<VaccineCardProps> = ({ user }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const { data: pets = [], isLoading: petsLoading } = useQuery<Pet[], Error>({
    queryKey: ['pets'], queryFn: getPets,
  });
  const { data: vaccinations = [], isLoading: vaccinationsLoading } = useQuery<Vaccination[], Error>({
    queryKey: ['vaccinations'], queryFn: getVaccinations,
  });

  // Klinik bilgilerini çek
  useEffect(() => {
    if (user?.clinicId) {
      getClinic(user.clinicId).then(setClinic).catch(() => {});
    }
  }, [user?.clinicId]);

  const addVaccineMutation = useMutation({
    mutationFn: addVaccination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vaccinations'] });
      toast.success('Aşı başarıyla eklendi!');
      setIsModalOpen(false);
    },
    onError: (err: any) => toast.error(`Hata: ${err.message}`),
  });

  const handleAddVaccine = (vaccineData: Omit<Vaccination, 'id' | 'createdAt' | 'pet'>) => {
    addVaccineMutation.mutate(vaccineData);
  };

  const loading = petsLoading || vaccinationsLoading;

  const filteredPets = pets.filter((pet) => {
    const q = normalizeTr(searchTerm);
    if (!q) return true;
    return normalizeTr(pet.name).includes(q) ||
      normalizeTr(pet.owner?.name).includes(q) ||
      normalizeTr(pet.breed).includes(q);
  });

  const petVaccinations = selectedPet
    ? vaccinations.filter((v) => v.pet_id === selectedPet.id)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    : [];

  // Yapılmış / planlı ayrımı tek durum fonksiyonuyla aynı kuralı (isDone) kullanır
  const pastVaccines = petVaccinations.filter(v => isDone(v));
  const futureVaccines = petVaccinations.filter(v => !isDone(v));

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const displayAge = (m: number) => {
    if (!m || m > 10000) return 'Belirtilmemiş';
    if (m < 12) return `${m} aylık`;
    const y = Math.floor(m / 12); const r = m % 12;
    return r === 0 ? `${y} yaşında` : `${y} yaş ${r} aylık`;
  };

  const speciesLabel = (s?: string) =>
    s === 'dog' ? 'Köpek' : s === 'cat' ? 'Kedi' :
    s === 'bird' ? 'Kuş' : s === 'rabbit' ? 'Tavşan' : 'Diğer';

  const handleDownloadPDF = () => {
    const input = printRef.current;
    if (!input || !selectedPet) return;
    toast.loading('PDF oluşturuluyor...', { id: 'pdf-toast' });

    const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-bg-card').trim() || '#ffffff';
    html2canvas(input, {
      scale: 2,
      backgroundColor: bgColor,
      useCORS: true,
      onclone: (_doc, clonedEl) => {
        clonedEl.querySelectorAll('.no-export').forEach((el) => {
          (el as HTMLElement).style.display = 'none';
        });
      },
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight);
      pdf.save(`${selectedPet.name}-asi-karti.pdf`);
      toast.success('PDF indirildi!', { id: 'pdf-toast' });
    }).catch(() => {
      toast.error('PDF oluşturulurken hata oluştu.', { id: 'pdf-toast' });
    });
  };

  const handlePrint = () => {
    const original = document.body.innerHTML;
    const printContent = printRef.current;
    if (!printContent) return;

    // .no-export elemanlarını gizleyerek kopya hazırla
    const cloned = printContent.cloneNode(true) as HTMLElement;
    cloned.querySelectorAll('.no-export').forEach(el => el.remove());

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Aşı Kartı</title>
      <style>
        body { font-family: 'Inter', sans-serif; margin: 0; padding: 20px; }
        ${Array.from(document.styleSheets).map(s => {
          try {
            return Array.from(s.cssRules).map(r => r.cssText).join('');
          } catch { return ''; }
        }).join('')}
      </style></head><body>${cloned.outerHTML}</body></html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  return (
    <div className="page-stack">
      {/* Page Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md shadow-purple-300/40">
              <ShieldCheck className="w-5 h-5" />
            </span>
            Aşı Kartı
          </h1>
          <p className="page-subtitle">Hastanın resmi aşı kartını oluştur, yazdır veya PDF olarak indir</p>
        </div>
      </div>

      {/* Layout: Sol = Hasta listesi, Sağ = Pasaport */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* Sol — Hasta Seçimi */}
        <div className="dribbble-panel p-4 lg:max-h-[calc(100vh-220px)] flex flex-col">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B0A99A] w-4 h-4" />
            <input
              type="text"
              placeholder="Hasta ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-9 py-2 text-sm"
            />
          </div>

          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? (
              <div className="text-center py-8 text-sm text-[#8A8676]">Yükleniyor...</div>
            ) : filteredPets.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#B0A99A]">Hasta bulunamadı</div>
            ) : (
              <div className="space-y-1">
                {filteredPets.map(pet => {
                  const count = vaccinations.filter(v => v.pet_id === pet.id).length;
                  const isSelected = selectedPet?.id === pet.id;
                  return (
                    <button
                      key={pet.id}
                      onClick={() => setSelectedPet(pet)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all text-left ${
                        isSelected
                          ? 'bg-[#E8EFD9] ring-2 ring-[#557236]'
                          : 'hover:bg-[#F7F4ED]'
                      }`}
                    >
                      <PetAvatar name={pet.name} species={pet.species} size={36} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isSelected ? 'text-[#3F5728]' : 'text-[#2B2B2B]'}`}>
                          {pet.name}
                        </p>
                        <p className="text-[11px] text-[#8A8676] truncate">
                          {pet.owner?.name || 'Sahipsiz'} · {pet.breed || speciesLabel(pet.species)}
                        </p>
                      </div>
                      {count > 0 && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-purple-100 text-[#3F5728] text-[10px] font-bold shrink-0">
                          <Syringe className="w-2.5 h-2.5" />
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sağ — Pasaport */}
        {!selectedPet ? (
          <div className="dribbble-panel">
            <EmptyState
              icon={ShieldCheck}
              iconColor="purple"
              title="Hasta seçin"
              description="Sol panelden bir hasta seçerek aşı pasaportunu görüntüleyin ve yazdırın."
            />
          </div>
        ) : (
          <div>
            {/* Aksiyon butonları — pasaport dışında */}
            <div className="no-export flex gap-2 mb-4 justify-end">
              <button onClick={() => setIsModalOpen(true)} className="btn btn-secondary btn-sm">
                <Syringe className="w-3.5 h-3.5" />
                Aşı Ekle
              </button>
              <button onClick={handleDownloadPDF} className="btn btn-secondary btn-sm">
                <Download className="w-3.5 h-3.5" />
                PDF İndir
              </button>
              <button onClick={handlePrint} className="btn btn-primary btn-sm">
                <Printer className="w-3.5 h-3.5" />
                Yazdır
              </button>
            </div>

            {/* PASAPORT — yazdırılabilir alan */}
            <motion.div
              ref={printRef}
              key={selectedPet.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg border border-[#E8E2D4]"
              style={{ pageBreakInside: 'avoid' }}
            >
              {/* Üst — Pasaport Başlık Şeridi (mor gradient) */}
              <div className="relative bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-8 py-6 text-white overflow-hidden">
                {/* Dekoratif çizgiler */}
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, white 0, white 1px, transparent 1px, transparent 12px)',
                }} />

                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] font-bold text-purple-200 uppercase">Veteriner Sağlık Belgesi</p>
                    <h2 className="text-2xl font-black mt-1 tracking-tight">AŞI KARTI</h2>
                    <p className="text-xs text-purple-200 mt-1">Vaccination Card</p>
                  </div>
                  <div className="text-right">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                      <ShieldCheck className="w-7 h-7" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Klinik Bilgisi */}
              {clinic && (
                <div className="bg-[#E8EFD9] border-b border-[#D2DEB4] px-8 py-3 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Stamp className="w-3.5 h-3.5 text-[#557236]" />
                    <span className="font-bold text-[#2F4420]">{clinic.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#3F5728]">
                    {clinic.phone && <span>{clinic.phone}</span>}
                    {clinic.email && <span className="hidden sm:inline">{clinic.email}</span>}
                  </div>
                </div>
              )}

              {/* Hasta Bilgisi Kartı */}
              <div className="px-8 py-6 border-b border-[#E8E2D4]">
                <p className="text-[10px] font-bold tracking-widest text-[#B0A99A] uppercase mb-3">Hasta Bilgileri / Patient Information</p>
                <div className="flex items-start gap-5">
                  <PetAvatar name={selectedPet.name} species={selectedPet.species} size={72} />
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">İsim</p>
                      <p className="font-bold text-[#2B2B2B] mt-0.5">{selectedPet.name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Tür</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">{speciesLabel(selectedPet.species)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Irk</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">{selectedPet.breed || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Yaş</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">{displayAge(Number(selectedPet.age || 0))}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Cinsiyet</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">
                        {selectedPet.gender === 'male' ? 'Erkek' :
                         selectedPet.gender === 'female' ? 'Dişi' : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Çip No</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">{selectedPet.protocolNo || '—'}</p>
                    </div>
                    <div className="col-span-2 md:col-span-3 pt-2 border-t border-[#F0EBE1]">
                      <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider">Sahibi / Owner</p>
                      <p className="font-semibold text-[#2B2B2B] mt-0.5">{selectedPet.owner?.name || 'Sahipsiz'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Yapılan Aşılar */}
              <div className="px-8 py-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-[#E8EFD9] flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#557236]" />
                  </div>
                  <h3 className="text-sm font-black text-[#2B2B2B] uppercase tracking-wider">Yapılan Aşılar</h3>
                  <span className="ml-auto text-[11px] font-bold text-[#8A8676] bg-[#F0EBE1] px-2 py-0.5 rounded-full">
                    {pastVaccines.length} kayıt
                  </span>
                </div>

                {pastVaccines.length === 0 ? (
                  <div className="text-center py-8 bg-[#F7F4ED] rounded-xl border border-dashed border-[#E8E2D4]">
                    <p className="text-sm text-[#B0A99A]">Yapılmış aşı kaydı yok</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pastVaccines.map((v, i) => (
                      <div
                        key={v.id}
                        className="grid grid-cols-[auto_1fr_auto_1fr] sm:grid-cols-[40px_1fr_120px_1.5fr] gap-3 items-center p-3 rounded-xl border-2 border-[#D2DEB4] bg-[#E8EFD9]/30"
                      >
                        {/* Numara */}
                        <div className="w-9 h-9 rounded-lg bg-[#557236] text-white font-black text-sm flex items-center justify-center shadow-md shadow-emerald-300/40">
                          {i + 1}
                        </div>
                        {/* Aşı Adı */}
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-[#557236] uppercase tracking-wide">Aşı</p>
                          <p className="text-sm font-bold text-[#2B2B2B] truncate">
                            {v.vaccine_name || v.name}
                          </p>
                        </div>
                        {/* Tarih */}
                        <div>
                          <p className="text-[10px] font-bold text-[#557236] uppercase tracking-wide">Tarih</p>
                          <p className="text-sm font-bold text-[#2B2B2B]">{formatDate(v.date)}</p>
                          {getDueDate(v) && (
                            <p className="text-[10px] text-[#8A8676] mt-0.5">Sonraki doz: {formatDate(getDueDate(v))}</p>
                          )}
                        </div>
                        {/* Notlar */}
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-[#557236] uppercase tracking-wide">Notlar</p>
                          <p className="text-xs text-[#8A8676] truncate" title={v.notes}>{v.notes || '—'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Planlanan Aşılar */}
              {futureVaccines.length > 0 && (
                <div className="px-8 pb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <h3 className="text-sm font-black text-[#2B2B2B] uppercase tracking-wider">Planlanan Aşılar</h3>
                    <span className="ml-auto text-[11px] font-bold text-[#8A8676] bg-[#F0EBE1] px-2 py-0.5 rounded-full">
                      {futureVaccines.length} kayıt
                    </span>
                  </div>

                  <div className="space-y-2">
                    {futureVaccines.map((v, i) => (
                      <div
                        key={v.id}
                        className="grid grid-cols-[auto_1fr_auto_1fr] sm:grid-cols-[40px_1fr_120px_1.5fr] gap-3 items-center p-3 rounded-xl border-2 border-amber-200 bg-amber-50/40"
                      >
                        <div className="w-9 h-9 rounded-lg bg-amber-500 text-white font-black text-sm flex items-center justify-center shadow-md shadow-amber-300/40">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Aşı</p>
                          <p className="text-sm font-bold text-[#2B2B2B] truncate">
                            {v.vaccine_name || v.name}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Planlanan</p>
                          <p className="text-sm font-bold text-[#2B2B2B]">{formatDate(v.date)}</p>
                          {getVaccinationStatus(v, vaccinations) === 'overdue' && (
                            <p className="text-[10px] font-bold text-red-600 mt-0.5">Gecikmiş</p>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Notlar</p>
                          <p className="text-xs text-[#8A8676] truncate" title={v.notes}>{v.notes || '—'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alt — Damga/Onay Şeridi */}
              <div className="bg-[#F7F4ED] border-t-2 border-dashed border-[#E8E2D4] px-8 py-5">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider mb-2">Klinik Onayı</p>
                    <div className="h-16 rounded-lg border-2 border-dashed border-[#E8E2D4] bg-white flex items-center justify-center">
                      <Stamp className="w-6 h-6 text-[#B0A99A]" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#B0A99A] uppercase tracking-wider mb-2">Veteriner İmza</p>
                    <div className="h-16 rounded-lg border-2 border-dashed border-[#E8E2D4] bg-white flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#B0A99A]" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-[#E8E2D4] flex items-center justify-between text-[10px] text-[#8A8676]">
                  <p>Belge Tarihi: <span className="font-semibold">{new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}</span></p>
                  <p className="font-mono">PASSPORT-{selectedPet.id.slice(0, 8).toUpperCase()}</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      <AddVaccineModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddVaccine}
        petId={selectedPet?.id || ''}
        isLoading={addVaccineMutation.isPending}
      />
    </div>
  );
};

export default VaccineCard;
